import { ComponentProps, ComponentRef, useEffect, useRef } from "react"
import { RichText, RichTextValue } from "@/components/rich-text"
import { SlideOnView } from "@/components/slide-on-view"
import { useInView, useMotionValue, useSpring } from "motion/react"

interface StatsProps {
  content: {
    title: RichTextValue
    description: RichTextValue
    stats: { label: string; value: number; afterValue: string }[]
  }
}

export function Stats({ content }: StatsProps) {
  return (
    <div className="mx-auto flex flex-col justify-around gap-10 bg-neutral-light px-4 py-12 md:flex-row md:gap-0 md:px-16 md:py-[10vh] lg:px-20 lg:py-[20vh]">
      <div className="max-w-[90rem]">
        <SlideOnView delay={0.3}>
          <RichText value={content.title} />
        </SlideOnView>
        <SlideOnView delay={0.6} className="mt-8">
          <RichText
            value={content.description}
            className="text-lg font-light xl:text-xlg"
          />
        </SlideOnView>
      </div>
      <div className="flex flex-wrap gap-10 md:flex-col md:gap-20">
        {content.stats.map((stat, i) => (
          <SlideOnView
            key={i}
            delay={0.3 * i}
            className="flex flex-col items-start"
          >
            <h3 className="text-6xl font-bold md:text-8xl xl:text-9xl">
              <AnimatedNumberCounter count={stat.value} />
              <span className="text-viola-600">{stat.afterValue}</span>
            </h3>
            <p className="max-w-75 text-center font-family-gthaptik text-xlg font-light md:text-left md:text-2xl">
              {stat.label}
            </p>
          </SlideOnView>
        ))}
      </div>
    </div>
  )
}

type AnimatedCountingNumberProps = ComponentProps<"span"> & {
  count: number
}
function AnimatedNumberCounter({
  count,
  className,
  ...props
}: AnimatedCountingNumberProps) {
  const ref = useRef<ComponentRef<"span">>(null)
  const numberStr = count.toString()
  const decimals = numberStr.includes(".")
    ? (numberStr.split(".")[1]?.length ?? 0)
    : 0
  const motionVal = useMotionValue(0)
  const springVal = useSpring(motionVal, { stiffness: 90, damping: 50 })
  const inView = useInView(ref, {
    once: true,
  })

  useEffect(() => {
    if (!inView) return
    motionVal.set(count)
    const unsubscribe = springVal.on("change", (latest) => {
      if (!ref.current) return
      const formatted =
        decimals > 0
          ? latest.toFixed(decimals).replace(".", ",")
          : Math.round(latest).toString()
      ref.current.textContent = formatted
    })
    return () => unsubscribe()
  }, [springVal, decimals, count, inView])

  const initialCount = "0" + (decimals > 0 ? "," + "0".repeat(decimals) : "")

  return (
    <span ref={ref} className={className} {...props}>
      {initialCount}
    </span>
  )
}
